/*
 * Copyright (c) 2021, WSO2 Inc. (http://www.wso2.com). All Rights Reserved.
 *
 * This software is the property of WSO2 Inc. and its suppliers, if any.
 * Dissemination of any information or reproduction of any material contained
 * herein is strictly forbidden, unless permitted by WSO2 in accordance with
 * the WSO2 Commercial License available at http://wso2.com/licenses.
 * For specific language governing the permissions and limitations under
 * this license, please see the license as well as any agreement you’ve
 * entered into with WSO2 governing the purchase of this software and any
 * associated services.
 */

import React, { ChangeEvent, Fragment, useState } from "react";
import { FormattedMessage } from 'react-intl';
import { connect } from "react-redux";

import { Button, Dialog, DialogActions, DialogContent, MenuItem, Select, Typography } from "@material-ui/core";
import classNames from "classnames";

import { ApiReducer, ApplicationState, Subscription, UserReducer } from "../../../data/interfaces";
import { fetchApiDetails, updateSubscription } from "../../../data/operations";

import { useStyles } from './styles';
import { PreLoaderLogo } from "../../../elements/PreLoaderLogo";

interface EditSubscriptionProps {
    open: boolean,
    onClose: () => void,
    subscription: Subscription,
    apiReducer: ApiReducer,
    userReducer: UserReducer,
    dispatchUpdateSubscription: (subscriptionId: string, subscription : Subscription, organizationId: string) => void,
    dispatchFetchApiDetails: (apiId: string) => void,
}

function EditSubscription(props: EditSubscriptionProps) {
    const {
        open, onClose, subscription, apiReducer, userReducer, dispatchFetchApiDetails, dispatchUpdateSubscription
    } = props;
    const classes = useStyles();
    const [throttlingPolicy, setThrottlingPolicy] = useState(subscription?.throttlingPolicy);
    const apiId = subscription.apiInfo?.id;
    const isAPIDetailsFetching = apiReducer.loading;
    const throttlingPolicies = apiReducer?.api?.tiers?.map((tier) => { return tier.tierName });

    const onThrottlingPolicyChange = (event: ChangeEvent<any>) => {
        setThrottlingPolicy(event.target.value);
    };

    const updateSubscription = () => {
        const organizationId = userReducer.selectedOrg.uuid;
        subscription.requestedThrottlingPolicy = throttlingPolicy;
        dispatchUpdateSubscription(subscription.subscriptionId, subscription, organizationId);
        onClose();
    };

    const DialogContents = () => {
        if (isAPIDetailsFetching) {
            return (
                <div className={classes.loaderContainer}>
                    <PreLoaderLogo size="medium" />
                </div>
            );
        } else {
            return (
                <Fragment>
                    <DialogContent className={classes.dialogContent}>
                        <div className={classes.contentAlign}>
                            <Typography className={classNames(classes.primaryTitle, classes.editPrimaryTitle)}>
                                <FormattedMessage
                                    id='application.subscriptions.editDialog.title'
                                    defaultMessage='Update Subscription'
                                />
                            </Typography>
                            <Typography className={classNames(classes.secondaryTitle, classes.editSecondaryTitle)}>
                                <FormattedMessage
                                    id='application.subscriptions.editDialog.subscriptionTier'
                                    defaultMessage='Current Subscription Tier: {tier}'
                                    values={{tier: subscription?.throttlingPolicy}}
                                />
                            </Typography>
                            <Select
                                variant="outlined"
                                value={throttlingPolicy}
                                onChange={onThrottlingPolicyChange}
                                className={classes.selectDropDownSm}
                                MenuProps={{
                                    getContentAnchorEl: null,
                                    classes: { paper: classes.dropdownStyle },
                                    anchorOrigin: {
                                        vertical: "bottom",
                                        horizontal: "left",
                                    }
                                }}
                                data-testid={"subscription-policy-selector"}
                            >
                                {throttlingPolicies?.map((policy) => (
                                    <MenuItem key={policy} value={policy}>
                                        {policy}
                                    </MenuItem>
                                ))}
                            </Select>
                        </div>
                    </DialogContent>
                    <DialogActions className={classNames(classes.dialogActions, classes.editDialogActions)}>
                        <Button
                            variant="contained"
                            color="default"
                            classes={{root: classes.editDialogButton}}
                            onClick={onClose}
                        >
                            <FormattedMessage
                                id='application.subscriptions.editDialog.cancelButton.label'
                                defaultMessage='Cancel'
                            />
                        </Button>
                        <Button
                            variant="contained"
                            color="primary"
                            classes={{root: classes.editDialogButton}}
                            onClick={updateSubscription}
                        >
                            <FormattedMessage
                                id='application.subscriptions.editDialog.updateButton.label'
                                defaultMessage='Update'
                            />
                        </Button>
                    </DialogActions>
                </Fragment>
            );
        }
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            onEnter={() => dispatchFetchApiDetails(apiId as string)}
            fullWidth={true}
            maxWidth="xs"
            classes={{paper: classes.dialogRootStyle}}
        >
            <DialogContents />
        </Dialog>
    );
}

const mapDispatchToProps = {
    dispatchUpdateSubscription: updateSubscription,
    dispatchFetchApiDetails: fetchApiDetails
};

const mapStateToProps = (state: ApplicationState) => {
    return {
        apiReducer: state.apiReducer,
        userReducer: state.userReducer
    }
};

export default connect(mapStateToProps, mapDispatchToProps)(EditSubscription);
